<template>
	<Loading v-if="store.isLoading" />

	<Row class="pb-0 !pt-8 bg-white flex-col">
		<div class="container xl:container mx-auto">
			<img
				src="/images/ts-blog-image.webp"
				alt=""
				class="max-w-[900px] w-full mx-auto mb-8"
				loading="lazy"
				width="900"
				height="472"
			/>
		</div>
	</Row>
	<Row class="pb-0 pt-0 bg-white category-row">
		<div class="xl:container gap-[80px]">
			<div
				ref="cateogryDropdown"
				class="category-container"
			>
				<a
					class="category-button"
					href="/blog/"
				>
					All
				</a>
				<a
					class="category-button"
					href="/category/guides/"
					data-slug="guides"
				>
					Guides
				</a>
				<a
					class="category-button"
					href="/category/success-stories/"
					data-slug="success-stories"
				>
					Success Stories
				</a>
				<a
					class="category-button"
					href="/category/patterns-to-short/"
					data-slug="patterns-to-short"
				>
					Patterns To Short
				</a>
				<a
					class="category-button"
					href="/category/lessons-from-students/"
					data-slug="lessons-from-students"
				>
					Lessons From Students
				</a>
				<a
					class="category-button"
					href="/category/mentor-update/"
					data-slug="mentor-update"
				>
					Mentor Updates
				</a>
				<div class="category-dropdown-box">
					<button
						class="category-button more"
						:class="showMoreCategories ? 'active' : null"
						@click="openDropdown"
					>
						More
						<svg
							width="15"
							height="10"
							viewBox="0 0 15 10"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M2.145 0.499999L7.5 6.06275L12.855 0.5L14.5 2.21255L7.5 9.5L0.5 2.21255L2.145 0.499999Z"
								fill="#64748B"
							/>
						</svg>
					</button>

					<div
						v-if="showMoreCategories"
						class="category-dropdown active"
					>
						<a
							v-for="itemCategory in categories"
							:key="itemCategory.id"
							class="category-button"
							:href="`/category/${itemCategory.slug}/`"
						>
							{{ itemCategory.name }}
						</a>
					</div>
				</div>
			</div>
		</div>
	</Row>
	<Row>
		<div class="xl:container gap-[80px]">
			<section
				v-if="posts && posts.length > 0 && !store.isLoading"
				id="blog-list"
				class="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[32px] px-[16px] md:px-[32px] xl:px-0"
			>
				<template
					v-for="(post, index) in posts"
					:key="post.id"
				>
					<PostTile
						:content="post.content.rendered"
						:source-url="post.image"
						:title="post.title.rendered"
						:slug="post.slug"
						:category="post.categories?.[0]"
						:data-page="post.page"
					/>
				</template>
			</section>

			<section
				v-else
				class="h-[900px]"
			/>
		</div>
	</Row>

	<div
		ref="loadMoreAnchor"
		class="blog-loading-container min-h-[10px]"
	>
		<div class="post-loading">
			<img
				v-if="showLoad"
				src="/images/loading.svg"
				class="loading animation"
			/>
		</div>
	</div>
</template>

<script setup>
import categories from "~/utils/categories-data.json";

const showAds = ref(false);

const props = defineProps({
	posts: {
		type: Array,
		required: true
	},
	currentPage: {
		type: Number,
		required: true
	},
	totalPages: {
		type: Number,
		required: true
	},
	previousPage: {
		type: Number,
		required: true
	},
	category: {
		type: Object,
		required: false,
		default: null
	}
});
const config = useRuntimeConfig();

const $route = useRoute();

const API_ENDPOINT = `${config.public.hostname}wp-json/wp/v2/posts/`;
const showLoad = ref(false);
// Initialize loadMore based on props immediately to prevent hydration issues
const loadMore = ref(props.currentPage < props.totalPages);
const cateogryDropdown = ref();
const loadMoreAnchor = ref();
const currentPage = ref(props.currentPage);
const posts = ref(props.posts);
const previousPage = ref(props.previousPage);
const showMoreCategories = ref(false);
const isPushStatePending = ref(false);
const route_name = ref("");

// IntersectionObserver for infinite scroll (works better on mobile and during hydration)
let infiniteScrollObserver = null;

const setupInfiniteScrollObserver = () => {
	if (!loadMoreAnchor.value || infiniteScrollObserver) return;

	infiniteScrollObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (
					entry.isIntersecting &&
					loadMore.value === true &&
					currentPage.value < props.totalPages
				) {
					showLoad.value = true;
					loadMore.value = false;
					loadMorePost();
				}
			});
		},
		{
			root: null,
			rootMargin: "200px", // Load more when within 200px of viewport
			threshold: 0
		}
	);

	infiniteScrollObserver.observe(loadMoreAnchor.value);
};

const cleanupInfiniteScrollObserver = () => {
	if (infiniteScrollObserver) {
		infiniteScrollObserver.disconnect();
		infiniteScrollObserver = null;
	}
};

const isElementInViewport = (element) => {
	if (!element) {
		return false;
	}

	const rect = element.getBoundingClientRect();
	return rect.bottom > 0 && rect.top < window.innerHeight;
};

const loadMorePost = async () => {
	currentPage.value += 1;
	const { data } = await fetchPostsList(
		API_ENDPOINT,
		currentPage.value,
		props.category?.id
	);

	const posts_formatted = data.map((post) =>
		postShapeFormatter(post, currentPage.value)
	);

	posts.value = [...posts.value, ...posts_formatted];

	setTimeout(() => (loadMore.value = true), 250);
};

const loadMorePostBackwards = async () => {
	loadMore.value = false;
	const { data } = await fetchPostsList(
		API_ENDPOINT,
		previousPage.value,
		props.category?.id
	);

	if (data) {
		const posts_formatted = data.map((post) =>
			postShapeFormatter(post, previousPage.value)
		);

		posts.value = [...posts_formatted, ...posts.value];

		setTimeout(() => {
			const total_gap_per_row = 32 * 5;
			const total_tile_height_per_row = 292 * 5;
			const total_blog_header_space = 44 + 172;

			window.scrollTo(
				0,
				total_tile_height_per_row + total_gap_per_row + total_blog_header_space
			);
			previousPage.value -= 1;
			loadMore.value = true;
		}, 250);
	}
};

const isAtTop = () => {
	return window.scrollY === 0;
};

const handleVisibilityChange = () => {
	const visibilityCheck = isElementInViewport(loadMoreAnchor.value);

	if (isAtTop() && loadMore.value === true && previousPage.value !== 0) {
		loadMorePostBackwards();
	}

	if (
		currentPage.value <= props.totalPages &&
		visibilityCheck &&
		loadMore.value === true
		// &&this.currentPage != this.totalPages
	) {
		showLoad.value = true;
		loadMore.value = false;
		loadMorePost();
	}
};

const openDropdown = () => {
	showMoreCategories.value = !showMoreCategories.value;
};

let pageObserverInstance = null;
const observedTiles = new Set();

const pageObserver = () => {
	if (!pageObserverInstance) {
		pageObserverInstance = new IntersectionObserver(
			function (entry) {
				if (entry[0].isIntersecting === true) {
					const page_num = Number(entry[0].target.getAttribute("data-page"));

					if (page_num && page_num > 0) {
						let url;

						const regex = /^\/blog\/page\/\d+\/$/;

						if (route_name.value === "/blog/" || regex.test(route_name.value)) {
							url = page_num === 1 ? "/blog/" : "/blog/page/" + page_num + "/";
						} else {
							let page_url = window.location.pathname;
							page_url = page_url.split("page")[0];

							url = page_num === 1 ? page_url : page_url + "page/" + page_num + "/";
						}

						if (!isPushStatePending.value) {
							history.replaceState({}, null, url);
						}
						isPushStatePending.value = true;
						setTimeout(() => {
							isPushStatePending.value = false;
						}, 500);
					}
				}
			},
			{ threshold: [0] }
		);
	}

	document.querySelectorAll(".post-tile").forEach((el) => {
		if (!observedTiles.has(el)) {
			observedTiles.add(el);
			pageObserverInstance.observe(el);
		}
	});
};

const scrollSuperThing = () => {
	// window.clearTimeout(isScrolling.value);
	// isScrolling.value = setTimeout(async () => {
	// }, 175);

	handleVisibilityChange();
	pageObserver();
};

watch(
	() => $route.name,
	(newPath, oldPath) => {
		if (process.client) {
			if (newPath !== oldPath) {
				window.removeEventListener("scroll", scrollSuperThing);
				cleanupInfiniteScrollObserver();
				if (pageObserverInstance) {
					pageObserverInstance.disconnect();
					pageObserverInstance = null;
					observedTiles.clear();
				}
			}

			showAds.value = window.adsAreWithUs;
		}
	}
);

// Watch loadMoreAnchor - recreate observer when element changes (e.g., after hydration mismatch)
watch(loadMoreAnchor, (newValue, oldValue) => {
	if (process.client && newValue) {
		// If element changed (old element was replaced), recreate the observer
		if (oldValue && newValue !== oldValue) {
			cleanupInfiniteScrollObserver();
		}
		if (!infiniteScrollObserver) {
			setupInfiniteScrollObserver();
		}
	}
}, { immediate: true });

// Cleanup on unmount
onUnmounted(() => {
	if (process.client) {
		window.removeEventListener("scroll", scrollSuperThing);
		cleanupInfiniteScrollObserver();
		if (pageObserverInstance) {
			pageObserverInstance.disconnect();
			pageObserverInstance = null;
			observedTiles.clear();
		}
	}
});

onMounted(async () => {
	if (process.client) {
		if (window.adsAreWithUs) {
			showAds.value = window.adsAreWithUs;
		}

		route_name.value = window.location.pathname;

		if (posts.value) {
			store.manualToggleLoading(false);
		}
		const container = cateogryDropdown.value;

		if (container) {
			// Check if we're on a category page
			// Handle both ref and unwrapped value cases
			const categorySlug = props.category?.value?.slug || props.category?.slug;
			
			if (categorySlug) {
				// Find the button with matching data-slug and highlight it
				const matchingButton = container.querySelector(`.category-button[data-slug="${categorySlug}"]`);
				if (matchingButton) {
					matchingButton.classList.add("active");
				}
			} else {
				// We're on the main blog page, highlight "All"
				const allButton = container.querySelector(".category-button");
				if (allButton) {
					allButton.classList.add("active");
				}
			}
		}

		window.addEventListener("scroll", scrollSuperThing, { passive: true });

		// Ensure loadMore is set correctly (in case props weren't available during initial ref creation)
		if (currentPage.value < props.totalPages && !loadMore.value) {
			loadMore.value = true;
		}

		// Setup IntersectionObserver for reliable infinite scroll
		await nextTick();
		setupInfiniteScrollObserver();
	}
});
</script>

<style lang="scss" scoped>
@import "@assets/utils.scss";

.category-row {
	@include breakpoint(lg) {
		position: sticky;
		top: 59px;
		z-index: 9;
	}
}

.router-link-exact-active {
	background-color: $primary-50;
	color: $primary-500 !important;
}

.category-button {
	display: inline-flex;
	padding: 12px 16px;
	justify-content: center;
	align-items: center;
	border: 1px solid $primary-50;
	color: $neutral-500;
	text-align: center;
	font-family: Rubik;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%;
	letter-spacing: 0.25px;
	gap: 8px;
	min-width: 96px;

	&.active {
		background-color: $primary-50;
		color: $primary-500;

		svg {
			transform: rotate(180deg);

			path {
				fill: $primary-500;
			}
		}
	}

	@include breakpoint(lg) {
		letter-spacing: 0.5px;

		padding: 12px 20px;
		font-size: 18px;
	}
}

.category-container {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
	padding: 0 16px;

	@include breakpoint(lg) {
		display: block;
		padding: 0;
	}
}

.category-dropdown-box {
	display: inline-block;

	button {
		background-color: $primary-50;
	}
}

.category-dropdown {
	display: flex;
	flex-wrap: wrap;
	gap: 12px;
}

.category-dropdown.active {
	padding: 12px 16px;
	position: absolute;
	left: 0;
	z-index: 1;
	width: 100vw;
	background-color: white;
	border-top: 1px solid $primary-500;
	// height: 200px;
	max-height: 320px;
	overflow-y: auto;
	max-width: 1200px;
	margin: auto;
	right: 0;

	.category-button {
		border-color: $neutral-300;
	}

	filter: drop-shadow(0px 12px 4px rgba(0, 0, 0, 0.2));

	@include breakpoint(lg) {
		border: 2px solid $primary-500;
		overflow-x: hidden;
		overflow-y: hidden;

		&:hover {
			overflow-y: auto;
		}

		border-radius: 8px;
	}
}

.category-button {
	border-color: $primary-50;
	text-align: left;
	padding: 12px 12px;

	&:hover {
		background-color: $primary-50;
		color: $primary-500;

		svg {
			path {
				fill: $primary-500;
			}
		}
	}

	@include breakpoint(lg) {
		padding-left: 16px;
		padding-right: 16px;
	}
}

.category-button.more.active {
	background-color: $primary-500;
	color: white;

	svg path {
		fill: white;
	}
}

.post-loading {
	padding: 64px 0;
	display: flex;
	justify-content: center;
}

.loading-gif {
	margin: auto;
	max-width: 64px;
}
</style>
